// Importing jwt-decode library for decoding JWT tokens
import jwtDecode from 'jwt-decode';
import segmentComponents from '../constants/segments_fields_components.json';

// Function to get initials from first and last name
export const getInitials = (firstName = '', lastName = '') =>
  `${firstName[0]}${lastName[0]}`.toLocaleUpperCase();

// Function to decode a JWT token and extract its payload
export const parseJWT = (jwt) => {
  return jwtDecode(jwt);
};

// Function to parse user information from a JWT token
export const parseUserInfo = (token) => {
  if (token) {
    const {
      given_name: firstName,
      family_name: lastName,
      picture: src,
      email_verified: emailVerified,
    } = parseJWT(token);
    return {
      firstName,
      lastName,
      emailVerified,
      src,
      token,
      permissions: {
        isAdmin: true, // Placeholder for admin permission
        isPhysician: true, // Placeholder for physician permission
      },
    };
  }
  return null;
};

// Function to check if an element is scrollable
export const isScrollable = function (ele) {
  const hasScrollableContent = ele.scrollHeight > ele.clientHeight; // Check if content overflows
  const overflowYStyle = window.getComputedStyle(ele).overflowY; // Get overflow-y style
  const isOverflowHidden = overflowYStyle.indexOf('hidden') !== -1; // Check if overflow is hidden

  return hasScrollableContent && !isOverflowHidden;
};

// Function to dynamically load Roboto font styles
export async function loadDefaultFonts() {
  try {
    const font300 = await import(`@fontsource/roboto/300.css`);
    const font400 = await import(`@fontsource/roboto/400.css`);
    const font500 = await import(`@fontsource/roboto/500.css`);
    const font700 = await import(`@fontsource/roboto/700.css`);
    font300.default();
    font400.default();
    font500.default();
    font700.default();
  } catch (error) {
    console.error(error); // Log error if font loading fails
  }
}

// Character set constants for generating random strings
export const charSet = {
  ALPHANUMERIC: 'alphanumeric',
  ALPHA: 'alpha',
  NUMERIC: 'numeric',
  ALPHA_UPPER: 'alphaUpper',
  ALPHA_LOWER: 'alphaLower',
};

// Function to generate a random string of specified length and type
export const guid = (len = 32, type = 'alphanumeric') => {
  const CHAR_SET_STRING = {
    alphanumeric:
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789',
    alpha: 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ',
    alphaUpper: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    alphaLower: 'abcdefghijklmnopqrstuvwxyz',
    numeric: '0123456789',
  };

  let code = [];
  const charSetString = CHAR_SET_STRING[type] || CHAR_SET_STRING.alphanumeric;

  for (let i = 0; i < len; i++) {
    const index = Math.floor(Math.random() * charSetString.length);
    code.push(charSetString.charAt(index));
  }
  return code.join('');
};

// Helper function to validate a value against a single validation rule
const validateFun = (validationRule, value) => {
  if (typeof validationRule === 'function') {
    return validationRule(value);
  }
  if (typeof validationRule === 'object') {
    return validationRule.test(value);
  }
  return true;
};

// Function to validate a value against a list of validation rules
export const validate = (value, validators = []) => {
  let isValid = false;
  for (let i = 0; i < validators.length; i++) {
    const validator = validators[i];
    isValid = validateFun(validator, value);
    if (isValid !== true) break; // Stop if a validation fails
  }
  return isValid;
};

// Function to mask a string with a specified character up to a maximum length
/**
 * Masks a string with a specified character up to a maximum length.
 *
 * @param {string} value - The string to be masked.
 * @param {string} maskWith - The character to use for masking. Defaults to '*'.
 * @param {number} maxLength - The maximum number of characters to mask. Defaults to Infinity.
 * @returns {string} The masked string. If the input value is falsy, an empty string is returned.
 */
export const mask = (value = '', maskWith = '*', maxLength = Infinity) => {
  if (typeof value !== 'string' || typeof maskWith !== 'string' || maskWith.length !== 1) {
    throw new Error('Invalid input: value must be a string and maskWith must be a single character string.');
  }
  const maskLength = Math.min(value.length, maxLength);
  return value.substring(0, maskLength).replace(/./g, maskWith) + value.substring(maskLength);
};

// Function to safely parse a JSON string to avoid runtime errors
export const parseJSONSafely = (jsonString) => {
  try {
    return JSON.parse(jsonString);
  } catch {
    return {}; // Return an empty object if parsing fails
  }
};

/**
 * A simple utility to escape HTML characters to prevent XSS.
 * @param {string} unsafe - The raw string to escape.
 * @returns {string} The escaped, safe string.
 */
export function escapeHtml(unsafe) {
  if (typeof unsafe !== 'string') return ''
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

/**
 * Formats a given string by splitting it into lines based on carriage return ('\r') 
 * and wrapping each line in a <span> element. Adds a <br /> element after each line 
 * except the last one.
 *
 * @param {string} code - The string to be formatted. If the input is not a string or is falsy, an empty array is returned.
 * @returns {Array<JSX.Element>} An array of JSX <span> elements representing the formatted lines of the input string.
 */
export const formatCode = (code) => {
  if (!code || typeof code !== 'string') return []
  const lines = code.split('\r');
  if (lines.length === 0) return []
  return lines.map((line, index) => (
    <span key={index}>
      {line}
      {line !== lines[lines.length - 1] ? <br /> : ''}
    </span>
  ))
}

export function getSegmentComponentDefinition(name, separator = '-') {
  if (!name) return null
  const [segment, field, component] = name.split(separator)
  const segmentData = segmentComponents[segment]
  if (!segmentData) return null
  if (field && component) {
    return segmentData[field]?.components?.[component]?.name || null
  }
  if (field) {
    return segmentData?.[field]?.name || null
  }
  return null
}

export function isJSON(value) {
  if (typeof value !== 'string') return false
  try {
    const parsed = JSON.parse(value)
    return typeof parsed === 'object' && parsed !== null
  } catch {
    return false
  }
}

// Function to format a connection code by splitting it into chunks of 4 characters and joining them with dashes
export const formatConnectionCode = (code) => {
  if (!code || typeof code !== 'string') return ''
  const fmt = () => {
    const chunks = []
    if (!code) {
      return
    }
    for (let i = 0; i < (code.length) / 4; i += 1) {
      const start = i * 4
      const end = (i + 1) * 4
      chunks.push(code.slice(start, end))
    }
    const formatted = chunks.join('-')
    return (
      formatted.toUpperCase()
    )

  }
  return fmt()
}